import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Truck, Search, Package, MapPin, Phone, User, Clock,
  CheckCircle, XCircle, Eye, Navigation, Calendar,
  ArrowRight, DollarSign, Bell
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLivraisons() {
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    delivered: 0,
    cancelled: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [ordersRes, couriersRes] = await Promise.all([
        axios.get(`${API}/orders?role=admin&limit=500`),
        axios.get(`${API}/couriers/approved`)
      ]);

      const ordersData = ordersRes.data;
      setOrders(ordersData);
      setCouriers(couriersRes.data);

      setStats({
        total: ordersData.length,
        pending: ordersData.filter(o => o.status === 'pending').length,
        inProgress: ordersData.filter(o => ['preparing', 'ready', 'assigned', 'picked_up', 'in_transit'].includes(o.status)).length,
        delivered: ordersData.filter(o => o.status === 'delivered').length,
        cancelled: ordersData.filter(o => o.status === 'cancelled').length
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const adminToken = JSON.parse(sessionStorage.getItem("admin_session") || "{}").token;
      await axios.put(`${API}/orders/${orderId}/status?status=${newStatus}`, null, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      preparing: "bg-blue-100 text-blue-800",
      ready: "bg-cyan-100 text-cyan-800",
      assigned: "bg-indigo-100 text-indigo-800",
      picked_up: "bg-purple-100 text-purple-800",
      in_transit: "bg-orange-100 text-orange-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    const labels = {
      pending: "En attente",
      preparing: "Préparation",
      ready: "Prête",
      assigned: "Assignée",
      picked_up: "Récupérée",
      in_transit: "En livraison",
      delivered: "Livrée",
      cancelled: "Annulée"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentBadge = (method, status) => {
    const methodLabels = {
      wave: "Wave",
      orange_money: "Orange Money",
      free_money: "Free Money",
      cash: "Espèces"
    };
    const isPaid = status === 'paid';
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${isPaid ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
        {methodLabels[method] || method} {isPaid ? '✓' : ''}
      </span>
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone?.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Package className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">En attente</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">En cours</p>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
              </div>
              <Truck className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Livrées</p>
                <p className="text-2xl font-bold">{stats.delivered}</p>
              </div>
              <CheckCircle className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Annulées</p>
                <p className="text-2xl font-bold">{stats.cancelled}</p>
              </div>
              <XCircle className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders List */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Gestion des Livraisons
            </CardTitle>
            <div className="flex flex-wrap gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  className="pl-9 w-48 rounded-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="h-10 px-4 rounded-full border border-input bg-background text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="preparing">En préparation</option>
                <option value="ready">Prêtes</option>
                <option value="assigned">Assignées</option>
                <option value="in_transit">En livraison</option>
                <option value="delivered">Livrées</option>
                <option value="cancelled">Annulées</option>
              </select>
              <Button onClick={fetchData} variant="outline" className="rounded-full">
                🔄
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Chargement...</div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>Aucune livraison trouvée</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="border rounded-xl overflow-hidden bg-white hover:shadow-md transition-all"
                >
                  {/* Order Header */}
                  <div className="p-4 border-b bg-slate-50">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-bold">#{order.id.substring(0, 8)}</p>
                          <p className="text-xs text-muted-foreground">
                            {order.created_at ? new Date(order.created_at).toLocaleString('fr-FR') : ''}
                          </p>
                        </div>
                        {getStatusBadge(order.status)}
                        {getPaymentBadge(order.payment_method, order.payment_status)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-primary">
                          {order.total_amount?.toLocaleString()} FCFA
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Order Body */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Customer */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">CLIENT</p>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{order.customer_phone}</p>
                      </div>

                      {/* Route */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">TRAJET</p>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="truncate">{order.pickup_address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm mt-1">
                          <Navigation className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="truncate">{order.delivery_address}</span>
                        </div>
                        {order.distance_km > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Distance: {order.distance_km.toFixed(1)} km
                          </p>
                        )}
                      </div>

                      {/* Courier */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">LIVREUR</p>
                        {order.courier_name ? (
                          <div>
                            <p className="font-medium">{order.courier_name}</p>
                            <p className="text-sm text-muted-foreground">{order.courier_phone}</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-muted-foreground">
                              {order.broadcast_status === 'open' ? (
                                <span className="text-[#D4AF37] font-bold flex items-center gap-1">
                                  <Bell className="w-3 h-3 animate-pulse" />
                                  Broadcast en cours…
                                </span>
                              ) : order.broadcast_status === 'exhausted' ? (
                                <span className="text-red-500 font-bold">
                                  Aucun livreur n'a accepté
                                </span>
                              ) : (
                                <span>Auto-dispatch en attente</span>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                        {order.status === 'pending' && (
                          <Button size="sm" className="rounded-full bg-blue-600" onClick={() => updateOrderStatus(order.id, 'preparing')}>
                            Démarrer préparation
                          </Button>
                        )}
                        {order.status === 'preparing' && (
                          <Button size="sm" className="rounded-full bg-cyan-600" onClick={() => updateOrderStatus(order.id, 'ready')}>
                            Marquer prête
                          </Button>
                        )}
                        {(order.status === 'ready' || order.status === 'assigned') && (
                          <Button size="sm" className="rounded-full bg-purple-600" onClick={() => updateOrderStatus(order.id, 'picked_up')}>
                            Récupérée
                          </Button>
                        )}
                        {order.status === 'picked_up' && (
                          <Button size="sm" className="rounded-full bg-orange-600" onClick={() => updateOrderStatus(order.id, 'in_transit')}>
                            En livraison
                          </Button>
                        )}
                        {order.status === 'in_transit' && (
                          <Button size="sm" className="rounded-full bg-green-600" onClick={() => updateOrderStatus(order.id, 'delivered')}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Livrée
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => updateOrderStatus(order.id, 'cancelled')}>
                          <XCircle className="w-4 h-4 mr-1" />
                          Annuler
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Expanded Details */}
                  {selectedOrder === order.id && (
                    <div className="border-t bg-slate-50 p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Frais de livraison</p>
                          <p className="font-semibold">{order.delivery_fee?.toLocaleString()} FCFA</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Commission plateforme</p>
                          <p className="font-semibold">{order.platform_commission?.toLocaleString()} FCFA</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Gain livreur</p>
                          <p className="font-semibold">{order.courier_earning?.toLocaleString()} FCFA</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Destinataire</p>
                          <p className="font-semibold">{order.recipient_name || '—'}</p>
                          <p className="text-xs">{order.recipient_phone || '—'}</p>
                        </div>
                      </div>
                      {order.notes && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-muted-foreground text-sm">Notes</p>
                          <p>{order.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
